import React, { useState, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import { useNavigate } from 'react-router-dom';
import { SocialStatusContainer } from './SocialComponents';
import { useSocialStatus } from './SocialStatusUpdater';
import { Play, Star, Menu, X, Heart, Video, Gamepad2, Headphones, User, MessageSquare, Calendar, MapPin, Globe } from "lucide-react";

const PublicUserProfile = memo(({ username }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState('Profile');
  const [bannerSrc, setBannerSrc] = useState(null);
  const [bannerFailed, setBannerFailed] = useState({ gif: false, png: false });
  const [glowing, setGlowing] = useState(true);
  const navigate = useNavigate();

  // Set up the glowing effect similar to the homepage
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowing(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const getDiscordAvatarUrl = (social) => {
    return social?.platform === 'discord' && social.avatarHash
      ? `https://cdn.discordapp.com/avatars/${social.socialId}/${social.avatarHash}.png?size=128`
      : null;
  };

  const getSteamAvatarUrl = (social) => {
    return social?.platform === 'steam' && social.avatarHash
      ? `https://steamcdn-a.akamaihd.net/steamcommunity/public/images/avatars/${social.avatarHash.substring(0, 2)}/${social.avatarHash}.jpg`
      : null;
  };

  const getDiscordBannerUrl = (social) => {
    if (social?.platform !== 'discord' || !social.bannerHash) return null;
    const baseUrl = `https://cdn.discordapp.com/banners/${social.socialId}/${social.bannerHash}`;
    return `${baseUrl}.gif?size=600`;
  };

  const getWebSocketDiscordAvatar = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.avatarUrl || null;
    }
    return null;
  };

  const getWebSocketDiscordBanner = (platform, socialId, statusData) => {
    if (platform === 'discord') {
      return statusData?.[platform]?.[socialId]?.bannerUrl || null;
    }
    return null;
  };

  const getSocialAvatar = (platform, socialId, social, statusData) => {
    if (platform === 'discord' && statusData?.[platform]?.[socialId]?.bannerUrl) {
      return getWebSocketDiscordAvatar('discord', socialId, statusData);
    } else if (platform === 'discord') {
      return getDiscordAvatarUrl(social);
    } else if (platform === 'steam') {
      return getSteamAvatarUrl(social);
    }
    return null;
  };

  const renderWebsite = (website) => {
    try {
      const parsedUrl = new URL(website);
      return (
        <a 
          href={website} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-cyan-400 hover:underline flex items-center"
        >
          <Globe size={16} className="mr-1" /> {parsedUrl.hostname}
        </a>
      );
    } catch (e) {
      return <span className="text-gray-300 flex items-center"><Globe size={16} className="mr-1" /> {website}</span>;
    }
  };

  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`https://api.4fingerstudios.com/api/auth/profile/${username}`);
        if (response.status === 404) {
          setError('User not found');
          setLoading(false);
          return;
        }
        if (!response.ok) throw new Error('Failed to fetch profile');
        const data = await response.json();
        setProfileData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchPublicProfile();
  }, [username]);

  const subscriptions = React.useMemo(() => {
    const subs = {};
    if (profileData?.socials) {
      const steamSocial = profileData.socials.find(s => s.platform === 'steam');
      const discordSocial = profileData.socials.find(s => s.platform === 'discord');
      if (steamSocial?.socialId) {
        subs.steam = [steamSocial.socialId];
      }
      if (discordSocial?.socialId) {
        subs.discord = [discordSocial.socialId];
      }
    }
    return subs;
  }, [profileData]);

  const { statusData, isConnecting, connectionError } = useSocialStatus(subscriptions, localStorage.getItem('token'));

  const discordSocial = profileData?.socials?.find(s => s.platform === 'discord');
  const steamSocial = profileData?.socials?.find(s => s.platform === 'steam');
  const discordId = subscriptions.discord?.[0];
  const discordStatus = discordId ? statusData.discord?.[discordId] : null;

  useEffect(() => {
    if (discordSocial) {
      // Prefer WebSocket data if available
      const wsBannerUrl = statusData?.discord?.[discordSocial.socialId]
        ? getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData)
        : null;
      
      if (wsBannerUrl) {
        setBannerSrc(wsBannerUrl);
        setBannerFailed({ gif: false, png: false });
      } else {
        // Fallback to profile data if WebSocket data is unavailable
        const profileBannerUrl = getDiscordBannerUrl(discordSocial);
        setBannerSrc(profileBannerUrl);
        setBannerFailed({ gif: false, png: false });
      }
    } else {
      setBannerSrc(null);
    }
  }, [statusData, profileData?.socials]);

  const handleBannerError = (type) => {
    if (type === 'gif' && !bannerFailed.gif) {
      setBannerFailed((prev) => ({ ...prev, gif: true }));
      if (discordSocial) {
        const wsBannerUrl = statusData?.discord?.[discordSocial.socialId]
          ? getWebSocketDiscordBanner('discord', discordSocial.socialId, statusData)
          : getDiscordBannerUrl(discordSocial);
        if (wsBannerUrl) {
          setBannerSrc(wsBannerUrl.replace('.gif', '.png'));
        } else {
          setBannerSrc(null);
        }
      }
    } else if (type === 'png' && !bannerFailed.png) {
      setBannerFailed((prev) => ({ ...prev, png: true }));
      setBannerSrc(null);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const tabContentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: 20, transition: { duration: 0.3 } },
  };

  const tabs = ['Profile', 'Stats', 'Socials', 'Gaming'];

  const profileImage = discordStatus?.avatar
    ? discordStatus.avatar
    : discordSocial && statusData?.discord?.[discordSocial.socialId]
    ? getWebSocketDiscordAvatar('discord', discordSocial.socialId, statusData)
    : steamSocial
    ? getSteamAvatarUrl(steamSocial)
    : profileData?.profileImageUrl || 'https://cdn.4fingerstudios.com/assets/images/istockphoto-518552551-612x612.jpg';

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Navbar - properly implemented */}
      <Navbar currentPage="PROFILE" />
      
      {/* Background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-blue-900 opacity-20"></div>
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-blue-400 blur-3xl opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-blue-500 blur-3xl opacity-20"></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 rounded-full bg-blue-300 blur-3xl opacity-10"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 pt-20">
        {error && (
          <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-red-900/80 text-red-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
            <X size={20} className="mr-2 flex-shrink-0" />
            <span className="flex-1">{error}</span>
          </div>
        )}

        {isConnecting && (
          <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-blue-900/80 text-blue-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="flex-1">Connecting to status service...</span>
          </div>
        )}

        {connectionError && (
          <div className="fixed top-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 bg-red-900/80 text-red-200 px-4 py-2 rounded-md shadow-lg flex items-center max-w-md mx-auto">
            <X size={20} className="mr-2 flex-shrink-0" />
            <span className="flex-1">{connectionError}</span>
          </div>
        )}

        <main className="relative pt-10 max-w-6xl mx-auto px-6 space-y-8">
          {loading ? (
            <div className="text-center text-cyan-400 text-lg">Loading profile...</div>
          ) : profileData ? (
            <>
              <motion.section 
                variants={cardVariants} 
                initial="initial"
                animate="animate"
                className="border-2 border-cyan-400 bg-black bg-opacity-70 p-8 rounded-lg"
              >
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className={`absolute inset-0 bg-cyan-400 opacity-20 rounded-full blur-lg ${glowing ? 'animate-pulse' : ''}`}></div>
                    <motion.img
                      src={profileImage}
                      alt="Profile"
                      className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-cyan-400 relative z-10"
                      whileHover={{ scale: 1.05 }}
                    />
                  </div>
                  
                  <div className="text-center mt-4 space-y-1">
                    <h1 className="text-2xl sm:text-3xl font-bold text-white">
                      {profileData.displayName || profileData.username}
                    </h1>
                    <p className="text-cyan-400 text-sm">@{profileData.username}</p>
                    
                    <p className="text-gray-300 text-sm sm:text-base mt-3">{profileData.bio || 'No bio yet.'}</p>
                    
                    <div className="flex flex-wrap justify-center gap-4 mt-4">
                      {profileData.location && (
                        <span className="text-gray-300 text-sm flex items-center">
                          <MapPin size={16} className="mr-1 text-cyan-400" /> {profileData.location}
                        </span>
                      )}
                      {profileData.website && renderWebsite(profileData.website)}
                    </div>
                  </div>
                </div>
              </motion.section>

              <div className="sticky top-16 z-10 bg-black/80 backdrop-blur-sm py-4 -mx-4 sm:mx-0">
                <div className="flex flex-wrap justify-center gap-2 sm:gap-4 border-b border-cyan-400/20">
                  {tabs.map((tab) => (
                    <motion.button
                      key={tab}
                      onClick={() => setCurrentTab(tab)}
                      className={`px-4 py-2 text-xs sm:text-sm uppercase tracking-wider rounded-md ${
                        currentTab === tab ? 'bg-cyan-400 text-black' : 'text-white hover:bg-cyan-400/10'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {tab}
                    </motion.button>
                  ))}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div 
                  key={currentTab} 
                  variants={tabContentVariants} 
                  initial="initial" 
                  animate="animate" 
                  exit="exit"
                >
                  {currentTab === 'Profile' && (
                    <motion.div 
                      className="bg-black/60 border-2 border-cyan-400/30 p-6 rounded-xl" 
                      variants={cardVariants}
                    >
                      <h3 className="text-lg font-bold mb-4 text-cyan-400">Profile Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm sm:text-base">
                        <p className="flex items-center text-gray-200">
                          <User size={16} className="mr-2 text-cyan-400" /> 
                          Display Name: {profileData.displayName || profileData.username}
                        </p>
                        <p className="flex items-center text-gray-200">
                          <MessageSquare size={16} className="mr-2 text-cyan-400" />
                          Username: @{profileData.username}
                        </p>
                        <p className="flex items-center text-gray-200">
                          <Heart size={16} className="mr-2 text-cyan-400" />
                          Bio: {profileData.bio || 'Not set'}
                        </p>
                        <p className="flex items-center text-gray-200">
                          <MapPin size={16} className="mr-2 text-cyan-400" />
                          Location: {profileData.location || 'Not set'}
                        </p>
                        <p className="flex items-center text-gray-200">
                          <Globe size={16} className="mr-2 text-cyan-400" />
                          Website: {profileData.website ? renderWebsite(profileData.website) : 'Not set'}
                        </p>
                      </div>
                    </motion.div>
                  )}
                  {currentTab === 'Stats' && (
                    <motion.div 
                      className="bg-black/60 border-2 border-cyan-400/30 p-6 rounded-xl" 
                      variants={cardVariants}
                    >
                      <h3 className="text-lg font-bold mb-4 text-cyan-400">Profile Stats</h3>
                      <ul className="space-y-4 text-sm sm:text-base">
                        <li className="text-gray-200 flex items-center">
                          <Calendar size={16} className="mr-2 text-cyan-400" />
                          <span>Joined on {new Date(profileData.createdAt).toLocaleDateString()}</span>
                        </li>
                        <li className="text-gray-200 flex items-center">
                          <Calendar size={16} className="mr-2 text-cyan-400" />
                          <span>Last updated on {new Date(profileData.updatedAt).toLocaleDateString()}</span>
                        </li>
                      </ul>
                    </motion.div>
                  )}
                  {currentTab === 'Socials' && (
                    <motion.div 
                      className="bg-black/60 border-2 border-cyan-400/30 p-6 rounded-xl" 
                      variants={cardVariants}
                    >
                      <h3 className="text-lg font-bold mb-4 text-cyan-400">Connected Socials</h3>
                      {profileData.socials && profileData.socials.length > 0 ? (
                        <ul className="space-y-4 text-sm sm:text-base">
                          {profileData.socials.map((social, index) => (
                            <li key={index} className="flex items-center space-x-2">
                              <span className="text-cyan-400">•</span>
                              <span className="flex items-center gap-2">
                                {getSocialAvatar(social.platform, social.socialId, social, statusData) && (
                                  <img
                                    src={getSocialAvatar(social.platform, social.socialId, social, statusData)}
                                    alt={`${social.platform} avatar`}
                                    className="w-6 h-6 rounded-full border border-cyan-400/30"
                                  />
                                )}
                                <span className="capitalize text-cyan-400">{social.platform}</span>
                                <span className="text-gray-300">: {social.displayName}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-400">No connected socials.</p>
                      )}
                    </motion.div>
                  )}
                  {currentTab === 'Gaming' && (
                    <motion.div 
                      variants={cardVariants}
                      className="bg-black/60 border-2 border-cyan-400/30 p-6 rounded-xl"
                    >
                      <h3 className="text-lg font-bold mb-4 text-cyan-400">Gaming Status</h3>
                      <SocialStatusContainer 
                        statusData={statusData}
                        isConnecting={isConnecting}
                        connectionError={connectionError}
                        profile={profileData}
                      />
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            </>
          ) : (
            <div className="text-center text-cyan-400 text-lg">Profile not found</div>
          )}
        </main>

        {/* Footer */}
      <footer className="bg-black py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className={`mb-8 ${glowing ? 'animate-pulse' : ''}`}>
              <div className="text-cyan-400 text-lg font-bold tracking-wider">THE MEDIOCRE MAKERS</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-bold mb-4">Navigate</h3>
                <ul className="space-y-2">
                  <li><a href="/" className="text-gray-400 hover:text-cyan-400 transition-colors">Home</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Social</h3>
                <ul className="space-y-2">
                  <li><a href="https://x.com/MediocreMakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Twitter</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Support</h3>
                <ul className="space-y-2">
                  <li><a href="https://linktr.ee/TheMediocreMakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Link Tree</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} The Mediocre Makers & 4FingerStudios. 
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
});

export default PublicUserProfile;